import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: all 0.3s ease;
`;

const ModalContainer = styled.div`
  width: 95vw;
  max-width: 400px;
  max-height: 90vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 15px;
  padding: 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s ease;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  display: block;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: dodgerblue;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: dodgerblue;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  box-sizing: border-box;
  
  option {
    background: #1e3c72;
    color: white;
  }
  
  &:focus {
    outline: none;
    border-color: dodgerblue;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ModalFooter = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => 
    props.variant === 'primary' ? '#007bff' :
    props.variant === 'success' ? '#28a745' :
    props.variant === 'cancel' ? 'transparent' : '#6c757d'
  };
  color: white;
  border: ${props => props.variant === 'cancel' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.2);
  border: 1px solid rgba(220, 53, 69, 0.5);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: #ff6b6b;
  font-size: 0.85rem;
`;

const SuccessMessage = styled.div`
  background: rgba(40, 167, 69, 0.2);
  border: 1px solid rgba(40, 167, 69, 0.5);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: #4fd69c;
  font-size: 0.85rem;
`;

const MobileBoardEditModal = ({ isOpen, onClose, boardData = null, onRefresh }) => {
  const [formData, setFormData] = useState({
    board_id: '',
    board_title: '',
    board_description: '',
    board_category: '',
    board_site_id: '1',
    board_read_auth: '1',
    board_write_auth: '1',
    board_idx: '0'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const isEditMode = !!boardData;

  const categories = [
    { value: 'root', label: '루트' },
    { value: 'Community', label: '커뮤니티' },
    { value: 'User', label: '협력사' },
    { value: 'Notice', label: '공지사항' },
    { value: 'FAQ', label: 'FAQ' }
  ];

  const authLevels = [
    { value: '1', label: '전체' },
    { value: '2', label: '회원' },
    { value: '3', label: '관리자' },
    { value: '4', label: '최고관리자' }
  ];

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && boardData) {
        setFormData({
          board_id: boardData.board_id || '',
          board_title: boardData.board_title || '',
          board_description: boardData.board_description || '',
          board_category: boardData.board_category || '',
          board_site_id: boardData.board_site_id || '1',
          board_read_auth: boardData.board_read_auth || '1',
          board_write_auth: boardData.board_write_auth || '1',
          board_idx: boardData.board_idx || '0'
        });
      } else {
        setFormData({
          board_id: '',
          board_title: '',
          board_description: '',
          board_category: '',
          board_site_id: '1',
          board_read_auth: '1',
          board_write_auth: '1',
          board_idx: '0'
        });
      }
      setError('');
      setSuccess('');
    }
  }, [isOpen, isEditMode, boardData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.board_id.trim()) {
      setError('게시판 ID를 입력해주세요.');
      return false;
    }
    
    if (!formData.board_title.trim()) {
      setError('게시판 제목을 입력해주세요.');
      return false;
    }
    
    if (!formData.board_category) {
      setError('카테고리를 선택해주세요.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = isEditMode ? '/request/update_board' : '/request/create_board';
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '요청 처리에 실패했습니다.');
      }

      const result = await response.json();
      setSuccess(isEditMode ? '게시판이 수정되었습니다.' : '게시판이 생성되었습니다.');
      
      setTimeout(() => {
        if (onRefresh) onRefresh();
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('Error saving board:', err);
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      board_id: '',
      board_title: '',
      board_description: '',
      board_category: '',
      board_site_id: '1',
      board_read_auth: '1',
      board_write_auth: '1',
      board_idx: '0'
    });
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose}>
      <ModalContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{isEditMode ? '게시판 수정' : '게시판 추가'}</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
            
            <FormGroup>
              <FormLabel>게시판 ID *</FormLabel>
              <FormInput
                type="text"
                name="board_id"
                value={formData.board_id}
                onChange={handleInputChange}
                placeholder="게시판 ID (영문/숫자)"
                disabled={isEditMode}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>게시판 제목 *</FormLabel>
              <FormInput
                type="text"
                name="board_title"
                value={formData.board_title}
                onChange={handleInputChange}
                placeholder="게시판 제목을 입력하세요"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>설명</FormLabel>
              <FormTextarea
                name="board_description"
                value={formData.board_description}
                onChange={handleInputChange}
                placeholder="게시판에 대한 설명을 입력하세요"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>카테고리 *</FormLabel>
              <FormSelect
                name="board_category"
                value={formData.board_category}
                onChange={handleInputChange}
                required
              >
                <option value="">카테고리를 선택하세요</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </FormSelect>
            </FormGroup>

            <FormRow>
              <FormGroup>
                <FormLabel>읽기 권한</FormLabel>
                <FormSelect
                  name="board_read_auth"
                  value={formData.board_read_auth}
                  onChange={handleInputChange}
                >
                  {authLevels.map(auth => (
                    <option key={auth.value} value={auth.value}>{auth.label}</option>
                  ))}
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>쓰기 권한</FormLabel>
                <FormSelect
                  name="board_write_auth"
                  value={formData.board_write_auth}
                  onChange={handleInputChange}
                >
                  {authLevels.map(auth => (
                    <option key={auth.value} value={auth.value}>{auth.label}</option>
                  ))}
                </FormSelect>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <FormLabel>사이트 ID</FormLabel>
                <FormInput
                  type="number"
                  name="board_site_id"
                  value={formData.board_site_id}
                  onChange={handleInputChange}
                  min="1"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>정렬 순서</FormLabel>
                <FormInput
                  type="number"
                  name="board_idx"
                  value={formData.board_idx}
                  onChange={handleInputChange}
                  min="0"
                />
              </FormGroup>
            </FormRow>
          </ModalBody>

          <ModalFooter>
            <ActionButton type="button" variant="cancel" onClick={handleClose}>
              취소
            </ActionButton>
            <ActionButton 
              type="submit" 
              variant="success" 
              disabled={loading}
            >
              {loading ? '처리중...' : (isEditMode ? '수정' : '추가')}
            </ActionButton>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default MobileBoardEditModal;